import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Save, Target } from "lucide-react";

export default function StaffTargets() {
  const [targets, setTargets] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, sRes] = await Promise.all([
        axios.get(`${API}/targets`),
        axios.get(`${API}/settings?lite=true`),
      ]);
      setTargets(tRes.data || []);
      setSettings(sRes.data || {});
    } catch (e) {
      toast.error('Failed to load targets');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateRow = (staffId, key, val) => {
    setTargets(prev => prev.map(r => r.staff_id === staffId ? { ...r, [key]: val } : r));
  };

  const saveRow = async (row) => {
    setSaving(s => ({ ...s, [row.staff_id]: true }));
    try {
      await axios.put(`${API}/targets/${row.staff_id}`, {
        default_monthly_target: Number(row.default_monthly_target || 0),
        monthly_overrides: row.monthly_overrides || {},
      });
      toast.success(`Saved target for ${row.staff_name}`);
    } catch (e) {
      toast.error('Failed to save');
    } finally {
      setSaving(s => ({ ...s, [row.staff_id]: false }));
    }
  };

  const currency = settings.currency_symbol || '£';
  const isSingle = (settings.business_mode || (targets.length <= 1 ? 'single' : 'team')) === 'single';

  return (
    <div className="space-y-5 fade-in max-w-4xl" data-testid="staff-targets-page">
      <Toaster position="top-center" />
      <div className="flex items-center gap-2">
        <Target size={20} className="text-primary" />
        <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Playfair Display' }}>Monthly Targets</h1>
      </div>
      <p className="text-sm text-muted-foreground">
        {isSingle
          ? 'Set your salon\'s monthly revenue target. Used to compute Targets vs Actuals.'
          : 'Set a monthly revenue target per team member. The salon target is the sum.'}
      </p>
      <Card className="p-4 rounded-2xl border shadow-sm">
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : targets.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="targets-empty">No active staff yet. Add staff first.</p>
        ) : (
          <div className="space-y-3">
            {targets.map(row => (
              <div key={row.staff_id} className="grid grid-cols-1 md:grid-cols-[1fr_220px_120px] gap-3 items-end border-b border-border pb-3 last:border-b-0" data-testid={`target-row-${row.staff_id}`}>
                <div>
                  <Label className="text-xs">Staff</Label>
                  <p className="text-sm font-medium mt-1">{row.staff_name}</p>
                </div>
                <div>
                  <Label className="text-xs">Default monthly target ({currency})</Label>
                  <Input
                    type="number"
                    min={0}
                    step={50}
                    value={row.default_monthly_target ?? 0}
                    onChange={e => updateRow(row.staff_id, 'default_monthly_target', e.target.value)}
                    className="mt-1 rounded-xl"
                    data-testid={`target-input-${row.staff_id}`}
                  />
                </div>
                <Button
                  size="sm"
                  className="rounded-xl"
                  onClick={() => saveRow(row)}
                  disabled={!!saving[row.staff_id]}
                  data-testid={`target-save-${row.staff_id}`}
                >
                  <Save size={14} className="mr-1" /> {saving[row.staff_id] ? 'Saving...' : 'Save'}
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
